"use client";

import { FormEvent, useState } from "react";
import ErrorAlert from "@/partials/ErrorAlert";
import SuccessMessage from "@/partials/SubmissionMessage";
import { Turnstile } from "@marsidev/react-turnstile";

const ContactUsForm = () => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData(event.currentTarget);

      if (!turnstileToken) {
        throw new Error("Please wait for verification to complete.");
      }

      formData.set("cf-turnstile-response", turnstileToken);

      const response = await fetch("/api/contact-form", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          data.message || "Unable to submit the form. Please try again.",
        );
      } else {
        setFormSubmitted(true); // Show the overlay after success
      }
    } catch (err) {
      const error = err as Error;
      setError(error.message); // Capture the error message to display to the user
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="animate-fade ease-in mx-auto md:col-6 mb-20 md:mb-0">
      {isFormSubmitted && (
        <SuccessMessage
          title="Thank you for submitting"
          return_to_link="/contact"
          image="/images/send-mail.png"
          message="Your form submission has been received."
          submessage="We will be in touch with you shortly."
        />
      )}

      <div>
        <div className="flex items-center md:col-6 col-7 pb-6 py-5 m-8">
          <div className="flex-grow border opacity-40 border-t border-light-green invisible lg:visible"></div>
          <h5 className="lg:mx-4 text-dark-green text-xl font-light tracking-widest">
            Let&apos;s Connect
          </h5>
          <div className="flex-grow border opacity-40 border-t border-light-green invisible lg:visible"></div>
        </div>

        <form method="POST" onSubmit={onSubmit}>
          <div className="flex flex-wrap md:-mx-6 mx-8">
            <div className="w-full md:w-1/2 px-1 md:mb-6 mb-3">
              <label htmlFor="fname" className="form-label text-dark-grey">
                First name <span className="text-red-500">*</span>
              </label>
              <input
                id="fname"
                name="fname"
                className="form-input bg-light-grey shadow-sm placeholder-gray-400 w-full h-12 border-mischka"
                placeholder="First name"
                type="text"
                required
              />
            </div>
            <div className="w-full md:w-1/2 px-1 md:mb-6 mb-3">
              <label htmlFor="lname" className="form-label text-dark-grey">
                Last name <span className="text-red-500">*</span>
              </label>
              <input
                id="lname"
                name="lname"
                className="form-input bg-light-grey shadow-sm placeholder-gray-400 w-full h-12 border-mischka"
                placeholder="Last name"
                type="text"
                required
              />
            </div>
            <div className="w-full md:w-1/2 px-1 md:mb-6 mb-3">
              <label htmlFor="email" className="form-label text-dark-grey">
                Email address <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                name="email"
                className="form-input bg-light-grey shadow-sm placeholder-gray-400 w-full h-12 border-mischka"
                placeholder="john.doe@email.com"
                type="email"
                required
              />
            </div>
            <div className="w-full md:w-1/2 px-1 md:mb-6 mb-3">
              <label htmlFor="interest" className="form-label text-dark-grey">
                Area of Interest <span className="text-red-500">*</span>
              </label>
              <input
                id="interest"
                name="interest"
                className="form-input bg-light-grey shadow-sm placeholder-gray-400 w-full h-12 border-mischka"
                placeholder="I am interested in..."
                required
              />
            </div>
            <div className="w-full px-1 md:mb-6 mb-3">
              <label htmlFor="message" className="form-label text-dark-grey">
                Anything else? <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                className="form-input bg-light-grey shadow-sm placeholder-gray-400 w-full border-mischka"
                placeholder="Leave a message..."
                rows={7}
                required
              ></textarea>
            </div>

            <div className="px-1">
              <Turnstile
                siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
                options={{
                  action: "contact",
                  appearance: "always",
                  theme: "light",
                  refreshExpired: "auto",
                  refreshTimeout: "auto",
                  retry: "auto",
                }}
                onSuccess={(token) => {
                  setTurnstileToken(token);
                  setError(null);
                }}
                onExpire={() => setTurnstileToken(null)}
                onError={(errorCode) => {
                  console.error("Turnstile error:", errorCode);

                  setTurnstileToken(null);
                  setError(
                    "Verification failed. Please refresh the page and try again.",
                  );
                }}
              />
              <button
                type="submit"
                className="btn btn-primary hover:bg-dark-grey hover:border-dark-grey shadow-sm w-full disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary disabled:hover:border-primary"
                disabled={isLoading || !turnstileToken}
              >
                <svg
                  aria-hidden="true"
                  className={`${
                    isLoading
                      ? "inline w-6 h-6 text-gray-200 animate-spin fill-blue-600 mr-2"
                      : "hidden"
                  }`}
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>

                {isLoading ? "Loading..." : "Send message"}
              </button>
            </div>
          </div>
        </form>

        <div className="pt-10">
          {error && <ErrorAlert error_message={error} />}
        </div>
      </div>
    </div>
  );
};

export default ContactUsForm;
